import type {
  AnnouncementDelivery,
  AnnouncementDetail,
  AnnouncementState,
  AnnouncementSummary,
  DeliveryStatus,
  PreviewStatus,
} from "../hooks/useAdminAnnouncements";

// Wording and arithmetic for the announcement pages. `tone` picks the class in
// AdminAnnouncements.module.css.

export type Tone = "good" | "busy" | "bad" | "muted";
export type Labelled = { label: string; tone: Tone };

export const STATE_LABEL: Record<AnnouncementState, Labelled> = {
  draft: { label: "Draft", tone: "muted" },
  sending: { label: "Sending", tone: "busy" },
  sent: { label: "Sent", tone: "good" },
  cancelled: { label: "Cancelled", tone: "muted" },
};

export const DELIVERY_LABEL: Record<DeliveryStatus, Labelled> = {
  not_sent: { label: "Not sent", tone: "muted" },
  queued: { label: "Queued", tone: "busy" },
  sending: { label: "Sending", tone: "busy" },
  delivered: { label: "Delivered", tone: "good" },
  failed: { label: "Failed", tone: "bad" },
  unreachable: { label: "No channel", tone: "bad" },
  unknown: { label: "Unknown", tone: "bad" },
  plugin_missing: { label: "Plugin missing", tone: "bad" },
  skipped: { label: "Skipped", tone: "muted" },
  cancelled: { label: "Cancelled", tone: "muted" },
};

export const PREVIEW_LABEL: Record<PreviewStatus, Labelled> = {
  pending: { label: "Checking", tone: "busy" },
  would_deliver: { label: "Reachable", tone: "good" },
  unreachable: { label: "No channel", tone: "bad" },
  failed: { label: "Check failed", tone: "bad" },
  plugin_missing: { label: "Plugin missing", tone: "bad" },
  skipped: { label: "Skipped", tone: "muted" },
};

// Which fallback step on the box chose the recipient: NotifyTarget.source in @ztc/plugin-kit/notify.
const SOURCE_LABEL: Record<string, string> = {
  config: "configured target",
  "main-session": "last used chat",
  session: "most recent chat",
  "only-channel": "only channel",
  dashboard: "dashboard chat",
};

/** What the customer actually reads: the plugin adds a fixed prefix. */
export const readsAs = (body: string): string => `ZeroToClaw: ${body.trim()}`;

const RETRYABLE: ReadonlySet<DeliveryStatus> = new Set(["failed", "unreachable", "plugin_missing", "skipped"]);
const NOT_ARRIVED: ReadonlyArray<DeliveryStatus> = ["failed", "unreachable", "unknown", "plugin_missing"];

export const describeWhere = (channel: string | null, target: string | null, source: string | null): string => {
  if (channel === null) return "-";
  const how = source === null ? "" : ` (${SOURCE_LABEL[source] ?? source})`;
  if (channel === "webchat") return `dashboard chat${how}`;
  return `${channel}${target === null ? "" : ` ${target}`}${how}`;
};

export const notArrivedCount = (summary: AnnouncementSummary): number =>
  NOT_ARRIVED.reduce((sum, status) => sum + (summary.counts[status] ?? 0), 0);

export const sendableCount = (rows: ReadonlyArray<AnnouncementDelivery>): number =>
  rows.filter((row) => row.status === "not_sent").length;

export const retryableCount = (rows: ReadonlyArray<AnnouncementDelivery>): number =>
  rows.filter((row) => RETRYABLE.has(row.status) && !row.inFlight).length;

/** Whether anything is still moving, which is when the detail view polls fast. */
export const isActive = (detail: AnnouncementDetail | null): boolean => {
  if (detail === null) return false;
  const draft = detail.announcement.state === "draft";
  return detail.deliveries.some(
    (row) =>
      row.inFlight || row.status === "queued" || row.status === "sending" || (draft && row.previewStatus === "pending"),
  );
};

export type CountTile = { label: string; value: number; tone: Tone };

/** The summary tiles: the dry run's verdict on a draft, the delivery outcome once sent. The
 *  first tile always shows; the rest only when non-zero. */
export const countTiles = (summary: AnnouncementSummary): CountTile[] => {
  const p = summary.previewCounts;
  const c = summary.counts;
  const tiles: CountTile[] =
    summary.state === "draft"
      ? [
          { label: "Reachable", value: p.would_deliver ?? 0, tone: "good" },
          { label: "Still checking", value: p.pending ?? 0, tone: "busy" },
          { label: "No channel", value: p.unreachable ?? 0, tone: "bad" },
          { label: "Plugin missing", value: p.plugin_missing ?? 0, tone: "bad" },
          { label: "Check failed", value: p.failed ?? 0, tone: "bad" },
          { label: "Skipped", value: p.skipped ?? 0, tone: "muted" },
        ]
      : [
          { label: "Delivered", value: c.delivered ?? 0, tone: "good" },
          { label: "In progress", value: (c.queued ?? 0) + (c.sending ?? 0), tone: "busy" },
          { label: "Failed", value: c.failed ?? 0, tone: "bad" },
          { label: "No channel", value: c.unreachable ?? 0, tone: "bad" },
          { label: "Plugin missing", value: c.plugin_missing ?? 0, tone: "bad" },
          { label: "Unknown", value: c.unknown ?? 0, tone: "bad" },
          { label: "Skipped", value: c.skipped ?? 0, tone: "muted" },
          { label: "Cancelled", value: c.cancelled ?? 0, tone: "muted" },
        ];
  return tiles.filter((tile, index) => index === 0 || tile.value > 0);
};
